// History — журнал изменений всего приложения: последние действия сверху, Load more, живой хвост; клик по объекту
// открывает его карточку, «Show on the grid» ведёт на Projects. Спека — docs/superpowers/specs/2026-09-13-history-design.md.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { data, useTable } from '@core/store';
import { useAuth } from '@core/auth/store';
import { ScreenToolbar } from '../roadmap/shared/Toolbar';
import { useTaskCard, useToast } from '../roadmap/shared/interaction';
import { useRoadmapTasks } from '../roadmap/shared/data';
import { requestJump } from '../roadmap/shared/spotlight';
import { TaskModal } from '../roadmap/shared/TaskModal';
import { EventModal, eventDraftOf, type EventDraft } from '../roadmap/shared/EventModal';
import { ProjectModal } from '../roadmap/projects/ProjectModal';
import { projectDraftOf, type ProjectDraft } from '../roadmap/projects/planner';
import { VacationModal } from '../roadmap/people/VacationModal';
import { vacationDraftOf, type VacationDraft } from '../roadmap/people/planner';
import { useLaunchTaskCard } from '../launch/useLaunchTaskCard';
import { HistoryList } from './HistoryList';
import { HistoryFilters } from './HistoryFilters';
import { useHistory, type HistoryFilter } from './useHistory';
import { useRevert } from './revert';
import { useActor, useResolve } from './resolve';
import type { HistoryRow } from './planner';

export function HistoryScreen() {
  const [filter, setFilter] = useState<HistoryFilter>({});
  const { actions, rows, loadMore, hasMore, loading, error, reload } = useHistory(filter);
  const resolve = useResolve();
  const actorOf = useActor();
  const navigate = useNavigate();
  const { fail } = useToast();
  useEffect(() => { data.updatePresence({ screen: 'history' }); }, []);
  const me = useAuth((s) => s.profile?.id ?? null);
  const profiles = useTable('profile');
  const actors = useMemo(() => [...profiles.keys()].map((id) => ({ id, ...actorOf(id) })).sort((a, b) => a.name.localeCompare(b.name)), [profiles, actorOf]);
  const { revertOf, revertActionOf, revertedBy } = useRevert({ rows, reload, fail });

  // карточки объектов — те же, что на экранах
  const { allTasks } = useRoadmapTasks();
  const { draft, openTask, closeModal, cardTab } = useTaskCard();
  const [eventDraft, setEventDraft] = useState<EventDraft | null>(null);
  const [projectDraft, setProjectDraft] = useState<ProjectDraft | null>(null);
  const [vacationDraft, setVacationDraft] = useState<VacationDraft | null>(null);
  const launchCard = useLaunchTaskCard();

  const open = useCallback((row: HistoryRow) => {
    const t = data.store.getState().tables;
    if (row.kind === 'delete') { fail('The object is deleted — use Restore'); return; }
    switch (row.table_name) {
      case 'task': { const x = t.task.get(row.row_id); if (x) openTask(x); return; }
      case 'event': { const x = t.event.get(row.row_id); if (x) setEventDraft(eventDraftOf(x)); return; }
      case 'project': { const x = t.project.get(row.row_id); if (x) setProjectDraft(projectDraftOf(x, t.subproject.values(), t.lane_project.values())); return; }
      case 'vacation': { const x = t.vacation.get(row.row_id); if (x) setVacationDraft(vacationDraftOf(x)); return; }
      case 'launch_task': { const x = t.launch_task.get(row.row_id); if (x) launchCard.open(x); return; }
      default: fail('No card for this object yet');
    }
  }, [openTask, fail, launchCard]);
  const jump = useCallback((row: HistoryRow) => { requestJump(row.row_id); void navigate('/roadmap/projects'); }, [navigate]);

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow border border-gray-300 overflow-hidden">
      <ScreenToolbar
        filter={<HistoryFilters value={filter} onChange={setFilter} me={me} actors={actors} />}
        status={<span className="text-sm text-gray-600" data-history-count={actions.length}>{actions.length} {actions.length === 1 ? 'action' : 'actions'} loaded</span>}
      />
      <div className="flex-1 overflow-y-auto scroller" data-history-body>
        <HistoryList actions={actions} resolve={resolve} actorOf={actorOf} hasMore={hasMore} loading={loading} error={error} onMore={loadMore}
          onOpen={open} onJump={jump} revert={revertOf} revertAction={revertActionOf} revertedBy={revertedBy} empty="No changes yet." />
      </div>
      {draft && <TaskModal draft={draft} allTasks={allTasks} onClose={closeModal} initialTab={cardTab} />}
      {eventDraft && <EventModal draft={eventDraft} onClose={() => setEventDraft(null)} />}
      {projectDraft && <ProjectModal draft={projectDraft} onClose={() => setProjectDraft(null)} />}
      {vacationDraft && <VacationModal draft={vacationDraft} onClose={() => setVacationDraft(null)} />}
      {launchCard.node}
    </div>
  );
}
